import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NomContentProvider, NomPostType } from '../../constants';

import { LoggerService } from '@services/logger.service';

// do not import videojs package as the imported version override the pluggins,
// this fixes the pluggins and typescript compolation. It's included in angular.json
declare var videojs: any;

@Component({
  selector: 'app-video-player',
  templateUrl: './video-player.component.html',
  styleUrls: ['./video-player.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class VideoPlayerComponent implements OnInit, OnChanges, OnDestroy {
  @Input() attachment: any;
  @Input() attachmentId: string;
  @Input() postId: string;
  @Input() postTypeId: NomPostType;
  @Input() contentProviderId: NomContentProvider;
  @Input() autoplay: boolean = false;
  @Input() width: number = 640;
  @Input() height: number = 390;

  @ViewChild('videoPlayer', { static: false }) videoPlayer!: ElementRef;
  @ViewChild('videoIframe', { static: true }) videoIframe!: ElementRef;

  private player: any;
  private observer: IntersectionObserver;
  protected videoInitialized: boolean = false;

  videoUrl: SafeResourceUrl;
  playerType: 'youtube' | 'video' | 'external' | 'sensika';
  isVisible = false;
  subtitleUrl: string = '';
  showIframe = false;
  videoReady: boolean = false;

  constructor(
    private sanitizer: DomSanitizer,
    private el: ElementRef,
    private logger: LoggerService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.determinePlayerType();
  }

  ngAfterViewInit() {
    this.updateVideoUrls();
    if (this.attachment.subtitles) {
      this.setupSubtitlesUrl();
    }

    // ! Lazy-init videojs / lazy-mount youtube iframe when the host scrolls into view.
    // ! Without this, every YouTube post in a long feed fetches the player bundle on load.
    if (
      this.playerType === 'video' ||
      this.playerType === 'sensika' ||
      this.playerType === 'youtube'
    ) {
      this.initializeObserver();
    } else {
      // ! immediately ready for external
      this.videoReady = true;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['attachment']) {
      this.updateVideoUrls();
    }
  }

  ngOnDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  private initializeObserver() {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.isVisible = true;
            if (this.playerType === 'youtube') {
              // ! Mounting the iframe is what triggers the YouTube player downloads.
              // ! Keep it mounted once shown so playback isn't torn down on scroll.
              if (!this.videoReady) {
                this.videoReady = true;
                this.cdr.detectChanges();
              }
            } else if (!this.videoInitialized) {
              this.initVideoPlayer();
            }
          } else {
            this.isVisible = false;
            this.pauseVideo();
          }
        });
      },
      { threshold: 0.1 }, // Adjust sensitivity if needed
    );

    this.observer.observe(this.el.nativeElement);
  }

  pauseVideo() {
    // Example for YouTube: send a postMessage command to pause the video
    if (this.playerType == 'external' && this.videoIframe) {
      const message = JSON.stringify({
        event: 'command',
        func: 'pauseVideo',
        args: [],
      });
      //alert('test');

      this.logger.debug('pauseVideo', message);

      this.videoIframe?.nativeElement.contentWindow.postMessage(message, '*');
    } else if (this.playerType == 'sensika' && this.videoPlayer) {
      this.videoPlayer.nativeElement.pause();
    }
  }

  private determinePlayerType() {
    switch (this.postTypeId) {
      case NomPostType.YouTubeVideo:
        this.playerType = 'youtube';
        if (
          this.attachment &&
          this.attachment.video_file_path &&
          this.attachment.video_file_path?.length > 0
        ) {
          this.playerType = 'sensika';
        }
        //console.log('YouTube video detected');
        //console.log(this.attachment);
        break;
      case NomPostType.BroadcastTVVideo:
      case NomPostType.BroadcastRadioAudio:
        if (this.contentProviderId == NomContentProvider.EMediaMonitor) {
          this.playerType = 'external';
        } else {
          this.playerType = 'sensika';
        }
        break;
      case NomPostType.InstagramPost:
        this.playerType = 'video';
        this.width = 250;
        this.height = null;
        break;
      default:
        this.playerType = 'video';
        break;
    }
  }

  private updateVideoUrls() {
    this.videoUrl = this.trustSrc(this.attachment);
  }

  private trustSrc(item: any): SafeResourceUrl {
    const u =
      item.video_file_path ||
      item.video_src ||
      item.video_url ||
      item.image ||
      '';
    const videoUrl = u.replace('http://', 'https://');
    return this.sanitizer.bypassSecurityTrustResourceUrl(videoUrl);
  }

  private setupSubtitlesUrl() {
    if (this.attachment && this.attachment.subtitles) {
      const subtitleBlob = new Blob([this.attachment.subtitles], {
        type: 'text/vtt',
      });
      this.subtitleUrl = URL.createObjectURL(subtitleBlob);
    }
  }

  initVideoPlayer(): void {
    if (!this.videoPlayer?.nativeElement) {
      this.logger.warn('Video element not ready');
      return;
    }

    this.videoInitialized = true;
    if (
      (this.playerType === 'video' || this.playerType === 'sensika') &&
      this.videoPlayer
    ) {
      const vjTranscribeOptions = {
        customClass: 'vjs-transcribe-pip',
        widgetId: 'vjs-transcribe-' + this.attachmentId,
        selector: true,
        selectorId: 'vjs-transcribe-selector-' + this.attachmentId,
        download: true,
        copy: true,
        downloadId: 'vjs-transcribe-download-' + this.attachmentId,
        search: true,
        searchId: 'vjs-transcribe-search-' + this.attachmentId,
        pip: true,
        pipId: 'vjs-transcribe-pip-' + this.attachmentId,
        mode: 'line',
        disablecc: false,
      };
      this.player = videojs(
        this.videoPlayer.nativeElement,
        {
          controls: true,
          autoplay: false,
          preload: 'none',
          width: 640,
          height: 364,
          plugins: {
            vjstranscribe: vjTranscribeOptions,
          },
        },
        () => {
          if (this.subtitleUrl) {
            this.player.addRemoteTextTrack(
              {
                kind: 'subtitles',
                src: this.subtitleUrl,
                srclang: 'en',
                label: 'Default Language',
              },
              false,
            );

            // Initialize the vjstranscribe plugin if we have subtitles using the typed API.
            // The plugin call will return a typed instance (if you have added your TypeScript definitions)
            try {
              this.player.vjstranscribe(vjTranscribeOptions).createTranscript();
            } catch (e) {
              this.logger.error('Error initializing vjstranscribe plugin', e);
            }
          }
          this.logger.debug('Video.js player is ready!');
          this.logger.debug(this);
          requestAnimationFrame(() => {
            this.videoReady = true;
            this.cdr.detectChanges();
          });
        },
      );
    }
  }
}
